import { Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@invent/wl-ui-kit-next/ui';
import { Paperclip, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { HelpPopover } from '@/design';
import { formatDateTime } from '@/domains/survey';
import { mailCampaignsApi } from '../../api/mail-campaigns-api';
import { LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { mailLogPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { TagChips } from '../mail-templates/template-tags';
import { CAMPAIGN_KINDS, CampaignStatusChip, variantWords } from './mail-words';

/**
 * One campaign beside the Campaigns tab (`?campaign=`): its template and version, who it reached
 * and who it skipped, its progress — read again every 15 seconds while it sends — its CC and
 * attachments, its recipients in the Sends tab, and Cancel for the mails not yet out (a draft is
 * deleted instead).
 */

const POLL_MS = 15_000;

export function CampaignSheet({
  campaignId,
  onClose,
  onChanged
}: {
  campaignId: Nullable<string>;
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Sheet open={campaignId !== null} onOpenChange={(open) => !open && onClose()} modal={false}>
      <SheetContent
        side="right"
        className="w-[min(28rem,calc(100vw-2rem))] gap-0 p-0"
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        data-campaign-sheet={campaignId ?? ''}
      >
        {campaignId && <CampaignBody campaignId={campaignId} onClose={onClose} onChanged={onChanged} />}
      </SheetContent>
    </Sheet>
  );
}

function CampaignBody({
  campaignId,
  onClose,
  onChanged
}: {
  campaignId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const read = useAdminData((signal) => mailCampaignsApi.get(campaignId, signal), [campaignId]);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const campaign = read.data;
  const live = campaign?.status === 'queued' || campaign?.status === 'sending';
  const reload = read.reload;

  React.useEffect(() => {
    if (!live) return undefined;
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, reload]);

  const act = async (run: () => Promise<unknown>, fallback: string, after: () => void) => {
    setBusy(true);
    setRefusal(null);
    try {
      await run();
      onChanged();
      after();
    } catch (error) {
      setRefusal(refusalMessage(error, fallback));
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <SheetHeader className="px-5 pt-5 pb-3">
      <SheetTitle className="flex items-center gap-1 text-[0.9375rem] text-[var(--color-royal-blue)]">
        {campaign?.name ?? 'Campaign'}
        <HelpPopover label="About a campaign" className="size-6">
          <p>
            The template&apos;s version was fixed when the campaign was sent. Its emails go out within
            the daily sending limits; Cancel stops the ones not yet out, and the rest are logged as
            skipped.
          </p>
        </HelpPopover>
      </SheetTitle>
      <SheetDescription>{campaign ? `${campaign.templateName} · ${variantWords(campaign.survey, campaign.version)}` : ''}</SheetDescription>
      <Button type="button" variant="ghost" size="icon" className="ml-auto size-8" aria-label="Close campaign" onClick={onClose}>
        <X className="size-4" aria-hidden />
      </Button>
    </SheetHeader>
  );

  if (read.error && !campaign) {
    return (
      <>
        {header}
        <div className="px-5">
          <LoadError message={read.error} />
        </div>
      </>
    );
  }
  if (!campaign) {
    return (
      <>
        {header}
        <div className="px-5">
          <TableSkeleton rows={4} />
        </div>
      </>
    );
  }
  // A draft's counts are zeros, not absent: it is drawn as whom it would reach.
  const counts = campaign.status === 'draft' ? null : campaign.counts;
  return (
    <>
      {header}
      <div className="grid content-start gap-4 overflow-y-auto px-5 pb-5 text-[0.8125rem]">
        <div className="flex flex-wrap items-center gap-2">
          <CampaignStatusChip status={campaign.status} />
          <span className="text-muted-foreground">{CAMPAIGN_KINDS[campaign.kind ?? ''] ?? campaign.kind}</span>
          <TagChips tags={campaign.tags} />
        </div>
        <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
          <dt>Recipients</dt>
          <dd className="text-right tabular-nums">{campaign.recipients.total.toLocaleString('en-US')}</dd>
          {counts ? (
            <>
              <dt className="font-medium">Sent</dt>
              <dd className="text-right font-medium tabular-nums">{counts.sent.toLocaleString('en-US')}</dd>
              <dt>Still to send</dt>
              <dd className="text-right tabular-nums">{counts.queued.toLocaleString('en-US')}</dd>
              <dt>Failed</dt>
              <dd className="text-right tabular-nums">{counts.failed.toLocaleString('en-US')}</dd>
              <dt className="text-muted-foreground">Skipped</dt>
              <dd className="text-right text-muted-foreground tabular-nums">{counts.skipped.toLocaleString('en-US')}</dd>
            </>
          ) : (
            <>
              <dt>Reached</dt>
              <dd className="text-right tabular-nums">{campaign.recipients.reachable.toLocaleString('en-US')}</dd>
              <dt className="text-muted-foreground">Unsubscribed · no address</dt>
              <dd className="text-right text-muted-foreground tabular-nums">
                {campaign.recipients.suppressed} · {campaign.recipients.noAddress}
              </dd>
            </>
          )}
        </dl>
        <Link
          to={mailLogPath({ campaign: campaign.id })}
          className="font-medium text-[var(--color-sapphire)] underline"
          data-campaign-recipients=""
        >
          Each recipient&apos;s email in Sends
        </Link>
        <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1">
          <dt className="text-muted-foreground">By</dt>
          <dd className="break-all">{campaign.createdBy?.email ?? '—'}</dd>
          <dt className="text-muted-foreground">Created</dt>
          <dd>{campaign.createdAt ? formatDateTime(campaign.createdAt) : '—'}</dd>
          <dt className="text-muted-foreground">Launched</dt>
          <dd>{campaign.launchedAt ? formatDateTime(campaign.launchedAt) : 'not yet'}</dd>
          {campaign.finishedAt && (
            <>
              <dt className="text-muted-foreground">Finished</dt>
              <dd>{formatDateTime(campaign.finishedAt)}</dd>
            </>
          )}
          <dt className="text-muted-foreground">CC</dt>
          <dd className="break-all">{campaign.cc.length ? campaign.cc.join(', ') : '—'}</dd>
          <dt className="text-muted-foreground">Replies</dt>
          <dd>{campaign.replyRouting ? 'Come back to the record' : "To the template's reply address"}</dd>
          {campaign.deliverTo && (
            <>
              <dt className="text-muted-foreground">Test inbox</dt>
              <dd className="break-all">{campaign.deliverTo}</dd>
            </>
          )}
        </dl>
        {campaign.attachments.length > 0 && (
          <ul className="grid gap-1" aria-label="Attachments">
            {campaign.attachments.map((attachment) => (
              <li key={attachment.id} className="flex items-center gap-1.5">
                <Paperclip className="size-3.5 text-muted-foreground" aria-hidden />
                <span className="break-all">{attachment.fileName}</span>
              </li>
            ))}
          </ul>
        )}
        {refusal && (
          <p role="alert" className="text-destructive">
            {refusal}
          </p>
        )}
        {live && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="justify-self-start"
            disabled={busy}
            onClick={() => {
              if (!window.confirm('Cancel the emails not yet sent? Those already out stay sent.')) return;
              void act(() => mailCampaignsApi.cancel(campaign.id), 'The campaign could not be cancelled.', () => void reload());
            }}
          >
            Cancel the rest
          </Button>
        )}
        {campaign.status === 'draft' && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="justify-self-start"
            disabled={busy}
            onClick={() => {
              if (!window.confirm('Delete this draft and its attachments?')) return;
              void act(() => mailCampaignsApi.remove(campaign.id), 'The draft could not be deleted.', onClose);
            }}
          >
            Delete the draft
          </Button>
        )}
      </div>
    </>
  );
}
